import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, Loader2, Check, X, GripVertical, CalendarDays, ScanLine, Upload, Wand2 } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const PX_PER_MIN = 1.3;
const SNAP = 5;

const PALETTE = [
  { bg: "rgba(245,158,11,0.14)",  border: "#F59E0B", text: "#FCD34D" }, // amber
  { bg: "rgba(225,29,72,0.14)",   border: "#E11D48", text: "#FDA4AF" }, // crimson
  { bg: "rgba(234,88,12,0.14)",   border: "#EA580C", text: "#FDBA74" }, // ember
  { bg: "rgba(120,113,108,0.16)", border: "#A8A29E", text: "#E7E5E4" }, // stone
  { bg: "rgba(217,119,6,0.14)",   border: "#D97706", text: "#FCD34D" }, // gold
  { bg: "rgba(190,18,60,0.14)",   border: "#BE123C", text: "#FECDD3" }, // rose deep
];

const subjectColor = (name) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

const fmt = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const toMin = (s) => { const [h, m] = s.split(":").map(Number); return h * 60 + m; };
const snap = (m) => Math.round(m / SNAP) * SNAP;

const Timetable = () => {
  const [slots, setSlots] = useState([]);
  const [pf, setPf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [showWeekend, setShowWeekend] = useState(false);
  const [modal, setModal] = useState(null); // {day, start, end, subject}
  const [scan, setScan] = useState(null); // { file, preview, busy, proposed: [], mode: 'replace'|'merge' }
  const dragRef = useRef(null); // {idx, grabOffsetMin}
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/timetable");
      setSlots(data.slots || []);
      setPf(data.period_format);
      if ((data.slots || []).some((s) => s.day > 4)) setShowWeekend(true);
    } catch (e) {
      toast.error("Failed to load timetable");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const persist = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.post("/timetable", { slots: next });
      setSlots(data.slots);
      setPf(data.period_format);
      setSavedAt(Date.now());
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      load();
      return false;
    } finally { setSaving(false); }
  };

  const baseMin = pf?.base_min || 35;
  const days = showWeekend ? [0, 1, 2, 3, 4, 5, 6] : [0, 1, 2, 3, 4];

  const [rangeStart, rangeEnd] = useMemo(() => {
    let lo = 8 * 60, hi = 16 * 60;
    for (const s of slots) { lo = Math.min(lo, s.start_minute); hi = Math.max(hi, s.end_minute); }
    return [Math.floor(lo / 60) * 60, Math.ceil(hi / 60) * 60];
  }, [slots]);
  const gridH = (rangeEnd - rangeStart) * PX_PER_MIN;
  const hours = useMemo(() => {
    const arr = [];
    for (let m = rangeStart; m <= rangeEnd; m += 60) arr.push(m);
    return arr;
  }, [rangeStart, rangeEnd]);

  const overlapCheck = (next, cand, skipIdx) => {
    for (let i = 0; i < next.length; i++) {
      if (i === skipIdx) continue;
      const s = next[i];
      if (s.day !== cand.day) continue;
      if (cand.start_minute < s.end_minute && s.start_minute < cand.end_minute) return i;
    }
    return -1;
  };

  // ---- actions ----
  const addPeriodTo = async (idx) => {
    const next = slots.map((s, i) => (i === idx ? { ...s, end_minute: s.end_minute + baseMin } : s));
    const hit = overlapCheck(next, next[idx], idx);
    if (hit >= 0 && next[hit].subject !== next[idx].subject) return toast.error(`Clashes with ${next[hit].subject}`);
    if (hit >= 0) {
      // merge into same-subject neighbour
      const merged = { ...next[idx], start_minute: Math.min(next[idx].start_minute, next[hit].start_minute), end_minute: Math.max(next[idx].end_minute, next[hit].end_minute) };
      const filtered = next.filter((_, i) => i !== idx && i !== hit).concat(merged);
      const hit2 = overlapCheck(filtered, merged, filtered.length - 1);
      if (hit2 >= 0 && filtered[hit2].subject.toLowerCase() !== merged.subject.toLowerCase()) return toast.error(`Merge would clash with ${filtered[hit2].subject}`);
      if (await persist(filtered)) toast.success(`${merged.subject} merged & extended`);
      return;
    }
    if (await persist(next)) toast.success(`+1 period → ${next[idx].subject}`);
  };

  const remove = async (idx) => { await persist(slots.filter((_, i) => i !== idx)); };

  const saveModal = async () => {
    if (!modal.subject.trim()) return toast.error("Subject required");
    const sm = toMin(modal.start), em = toMin(modal.end);
    if (em <= sm) return toast.error("End must be after start");
    const cand = { day: modal.day, start_minute: sm, end_minute: em, subject: modal.subject.trim() };
    const next = [...slots, cand];
    const hit = overlapCheck(next, cand, next.length - 1);
    if (hit >= 0 && next[hit].subject.toLowerCase() !== cand.subject.toLowerCase()) return toast.error(`Clashes with ${next[hit].subject} (${fmt(next[hit].start_minute)}–${fmt(next[hit].end_minute)})`);
    setModal(null);
    if (await persist(next)) toast.success(`${cand.subject} added`);
  };

  const openAdd = (day = 0, startMin = 9 * 60) => {
    setModal({ day, start: fmt(startMin), end: fmt(startMin + baseMin), subject: "" });
  };

  // ---- drag & drop ----
  const onDragStart = (e, idx) => {
    const rect = e.currentTarget.getBoundingClientRect();
    dragRef.current = { idx, grabOffsetMin: snap((e.clientY - rect.top) / PX_PER_MIN) };
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(idx));
  };

  const onDropDay = async (e, day) => {
    e.preventDefault();
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const cur = slots[drag.idx];
    const dur = cur.end_minute - cur.start_minute;
    let start = snap(rangeStart + (e.clientY - rect.top) / PX_PER_MIN - drag.grabOffsetMin);
    start = Math.max(0, Math.min(start, 24 * 60 - dur));
    const cand = { ...cur, day, start_minute: start, end_minute: start + dur };
    if (cand.day === cur.day && cand.start_minute === cur.start_minute) return;
    let next = slots.map((s, i) => (i === drag.idx ? cand : s));
    const hit = overlapCheck(next, cand, drag.idx);
    if (hit >= 0) {
      if (next[hit].subject.toLowerCase() !== cand.subject.toLowerCase()) return toast.error(`Clashes with ${next[hit].subject}`);
      const merged = { ...cand, start_minute: Math.min(cand.start_minute, next[hit].start_minute), end_minute: Math.max(cand.end_minute, next[hit].end_minute) };
      next = next.filter((_, i) => i !== drag.idx && i !== hit).concat(merged);
      const hit2 = overlapCheck(next, merged, next.length - 1);
      if (hit2 >= 0 && next[hit2].subject.toLowerCase() !== merged.subject.toLowerCase()) return toast.error(`Merge would clash with ${next[hit2].subject}`);
      if (await persist(next)) toast.success(`${merged.subject} blocks merged into ${Math.max(1, Math.round((merged.end_minute - merged.start_minute) / baseMin))} periods`);
      return;
    }
    if (await persist(next)) toast.success(`${cand.subject} → ${DAYS[day]} ${fmt(start)}`);
  };

  const onColumnClick = (e, day) => {
    if (e.target !== e.currentTarget) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const start = Math.max(rangeStart, snap(rangeStart + (e.clientY - rect.top) / PX_PER_MIN));
    openAdd(day, start);
  };

  // ---- ai image scanner ----
  const onScanFile = (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error("Please upload a PNG, JPG or WEBP image");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is larger than 8MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setScan((s) => ({ ...(s || {}), file, preview: reader.result, proposed: [], mode: s?.mode || "merge" }));
    reader.readAsDataURL(file);
  };

  const runScan = async () => {
    if (!scan?.file) return;
    setScan((s) => ({ ...s, busy: true }));
    try {
      const fd = new FormData();
      fd.append("image", scan.file, scan.file.name);
      const { data } = await api.post("/timetable/scan", fd);
      const proposed = data.slots || [];
      if (proposed.length === 0) {
        toast.error("No periods detected — try a clearer image");
      } else {
        toast.success(`Found ${proposed.length} period${proposed.length === 1 ? "" : "s"}`);
      }
      setScan((s) => ({ ...s, busy: false, proposed }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
      setScan((s) => ({ ...s, busy: false }));
    }
  };

  const applyScan = async () => {
    if (!scan?.proposed?.length) return;
    let next;
    if (scan.mode === "replace") {
      next = scan.proposed;
    } else {
      // merge: append proposed slots that don't overlap different-subject existing slots
      next = [...slots];
      let skipped = 0;
      for (const p of scan.proposed) {
        const cand = { ...p };
        const hit = overlapCheck(next, cand, -1);
        if (hit >= 0 && next[hit].subject.toLowerCase() !== cand.subject.toLowerCase()) {
          skipped += 1;
          continue;
        }
        if (hit >= 0) {
          // merge with same-subject
          const merged = {
            ...cand,
            start_minute: Math.min(cand.start_minute, next[hit].start_minute),
            end_minute: Math.max(cand.end_minute, next[hit].end_minute),
          };
          next = next.filter((_, i) => i !== hit).concat(merged);
        } else {
          next.push(cand);
        }
      }
      if (skipped) toast.message(`${skipped} block${skipped === 1 ? "" : "s"} skipped due to clashes`);
    }
    setScan((s) => ({ ...s, busy: true }));
    const ok = await persist(next);
    if (ok) {
      toast.success("Timetable updated from image");
      setScan(null);
    } else {
      setScan((s) => ({ ...s, busy: false }));
    }
  };

  const removeProposed = (idx) => {
    setScan((s) => ({ ...s, proposed: s.proposed.filter((_, i) => i !== idx) }));
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <header className="mb-8">
        <div className="label-mono mb-3 flex items-center gap-3">
          <span>/ timetable</span>
          {saving && <span className="text-amber-500 flex items-center gap-1" data-testid="saving-indicator"><Loader2 size={10} className="animate-spin"/> SAVING</span>}
          {!saving && savedAt && <span className="text-neutral-500 flex items-center gap-1" data-testid="saved-indicator"><Check size={10}/> SAVED</span>}
        </div>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-4xl md:text-5xl tightest font-semibold leading-none">Smart schedule.</h1>
            <p className="text-neutral-500 mt-3 max-w-2xl text-[0.88rem] leading-relaxed">
              Click any empty space to add a period · drag blocks to move them · drop onto a same-subject block to merge · hit <span className="text-white">+1</span> to extend.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {pf && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-white/[0.08] bg-white/[0.02] text-neutral-300 text-[0.72rem] font-mono rounded-[2px]" data-testid="period-format">
                <Sparkles size={11}/> base = {pf.base_min}m
              </div>
            )}
            <button onClick={() => setShowWeekend(!showWeekend)} className="btn-ghost inline-flex items-center gap-2 text-[0.75rem]" data-testid="toggle-weekend">
              <CalendarDays size={12}/> {showWeekend ? "Hide" : "Show"} weekend
            </button>
            <button
              onClick={() => setScan({ file: null, preview: null, busy: false, proposed: [], mode: "merge" })}
              className="btn-secondary text-[0.75rem] py-2 px-3.5 inline-flex items-center gap-2"
              data-testid="open-scan"
            >
              <ScanLine size={13}/> Scan image
            </button>
            <button onClick={() => openAdd()} className="btn-primary" data-testid="open-add-period">
              <Plus size={13}/> Add period
            </button>
          </div>
        </div>
      </header>

      {loading ? (
        <div className="glass p-10 text-neutral-500 text-[0.86rem]">Loading…</div>
      ) : (
        <div className="glass p-4 md:p-6 overflow-x-auto" data-testid="week-grid">
          <div className="min-w-[720px]">
            {/* day headers */}
            <div className="flex">
              <div className="w-14 shrink-0"/>
              {days.map((d) => (
                <div key={d} className="flex-1 text-center pb-3 label-mono">{DAYS[d]}</div>
              ))}
            </div>
            <div className="flex">
              {/* time gutter */}
              <div className="w-14 shrink-0 relative" style={{ height: gridH }}>
                {hours.map((m) => (
                  <div key={m} className="absolute right-2 -translate-y-1/2 text-[10px] font-mono text-white/30" style={{ top: (m - rangeStart) * PX_PER_MIN }}>{fmt(m)}</div>
                ))}
              </div>
              {/* day columns */}
              {days.map((d) => (
                <div
                  key={d}
                  className="flex-1 relative border-l border-white/5 cursor-crosshair hover:bg-white/[0.02] transition-colors"
                  style={{ height: gridH }}
                  onClick={(e) => onColumnClick(e, d)}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                  onDrop={(e) => onDropDay(e, d)}
                  data-testid={`day-col-${d}`}
                >
                  {hours.map((m) => (
                    <div key={m} className="absolute left-0 right-0 border-t border-white/5 pointer-events-none" style={{ top: (m - rangeStart) * PX_PER_MIN }}/>
                  ))}
                  {slots.map((s, idx) => {
                    if (s.day !== d) return null;
                    const c = subjectColor(s.subject);
                    const dur = s.end_minute - s.start_minute;
                    const count = Math.max(1, Math.round(dur / baseMin));
                    const h = dur * PX_PER_MIN;
                    return (
                      <div
                        key={`${idx}-${s.start_minute}`}
                        draggable
                        onDragStart={(e) => onDragStart(e, idx)}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute left-1 right-1 group cursor-grab active:cursor-grabbing select-none backdrop-blur-sm transition-shadow hover:shadow-[0_0_20px_rgba(255,255,255,0.08)]"
                        style={{ top: (s.start_minute - rangeStart) * PX_PER_MIN, height: Math.max(h, 26), background: c.bg, borderLeft: `2px solid ${c.border}`, border: `1px solid ${c.border}40`, borderLeftWidth: 3, borderLeftColor: c.border }}
                        data-testid={`block-${DAYS[d]}-${s.subject.replace(/\s+/g, "-")}`}
                      >
                        <div className="p-1.5 h-full flex flex-col overflow-hidden">
                          <div className="flex items-start justify-between gap-1">
                            <div className="min-w-0">
                              <div className="font-display text-[11px] leading-tight truncate" style={{ color: c.text }}>{s.subject}</div>
                              {h > 40 && <div className="font-mono text-[9px] text-white/40">{fmt(s.start_minute)}–{fmt(s.end_minute)}</div>}
                            </div>
                            <GripVertical size={11} className="text-white/20 shrink-0 mt-0.5"/>
                          </div>
                          {h > 58 && <div className="text-[9px] font-display mt-auto" style={{ color: c.text }}>{count} period{count > 1 ? "s" : ""}</div>}
                        </div>
                        <div className="absolute -right-1 -top-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); addPeriodTo(idx); }}
                            disabled={saving}
                            title="+1 period"
                            className="px-1.5 py-0.5 text-[9px] font-display bg-black border disabled:opacity-40"
                            style={{ borderColor: c.border, color: c.text }}
                            data-testid={`plus-period-${idx}`}
                          >+1</button>
                          <button
                            onClick={(e) => { e.stopPropagation(); remove(idx); }}
                            disabled={saving}
                            className="p-0.5 bg-black border border-white/20 text-white/40 hover:text-[#FF007F] hover:border-[#FF007F] disabled:opacity-40"
                            data-testid={`remove-slot-${idx}`}
                          ><Trash2 size={10}/></button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          {slots.length === 0 && (
            <div className="text-center text-neutral-500 text-[0.86rem] py-4" data-testid="empty-hint">No periods yet — click anywhere on the grid or hit <span className="text-white">Add period</span>.</div>
          )}
        </div>
      )}

      {/* add period modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4" onClick={() => setModal(null)} data-testid="add-modal">
          <div className="glass-strong p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div className="font-display text-lg tightest font-semibold">Add period</div>
              <button onClick={() => setModal(null)} className="text-neutral-500 hover:text-white transition-colors" data-testid="modal-close"><X size={16}/></button>
            </div>
            <div className="space-y-4">
              <div>
                <div className="label-mono mb-2">Subject</div>
                <input
                  autoFocus
                  placeholder="e.g. Maths"
                  value={modal.subject}
                  onChange={(e) => setModal({ ...modal, subject: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveModal(); } }}
                  className="input-cyber"
                  data-testid="modal-subject"
                />
              </div>
              <div>
                <div className="label-mono mb-2">Day</div>
                <select value={modal.day} onChange={(e) => setModal({ ...modal, day: Number(e.target.value) })} className="input-cyber" data-testid="modal-day">
                  {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="label-mono mb-2">Start</div>
                  <input type="time" value={modal.start} onChange={(e) => { const st = e.target.value; setModal({ ...modal, start: st, end: fmt(toMin(st) + baseMin) }); }} className="input-cyber" data-testid="modal-start"/>
                </div>
                <div>
                  <div className="label-mono mb-2">End</div>
                  <input type="time" value={modal.end} onChange={(e) => setModal({ ...modal, end: e.target.value })} className="input-cyber" data-testid="modal-end"/>
                </div>
              </div>
              <div className="text-[0.72rem] text-neutral-500 font-mono">
                duration: {Math.max(0, toMin(modal.end) - toMin(modal.start))}m ≈ {Math.max(1, Math.round(Math.max(0, toMin(modal.end) - toMin(modal.start)) / baseMin))} period(s)
              </div>
              <button onClick={saveModal} disabled={saving} className="btn-primary w-full disabled:opacity-50" data-testid="modal-save">
                {saving ? <Loader2 size={13} className="animate-spin"/> : <Plus size={13}/>} Add period
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ai image scanner */}
      {scan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4" onClick={() => !scan.busy && setScan(null)} data-testid="scan-modal">
          <div className="glass-strong w-full max-w-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-5 border-b border-white/[0.06] flex items-center justify-between sticky top-0 bg-black/70 backdrop-blur-xl">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 border border-white/[0.08] bg-white/[0.02] flex items-center justify-center rounded-[2px]">
                  <ScanLine size={14} className="text-amber-400" />
                </div>
                <div>
                  <div className="font-display text-base tightest font-semibold leading-none">Scan timetable</div>
                  <div className="text-[0.72rem] text-neutral-500 mt-0.5">Upload a photo or screenshot — GPT-5.2 will extract every period.</div>
                </div>
              </div>
              <button onClick={() => !scan.busy && setScan(null)} className="text-neutral-500 hover:text-white transition-colors" data-testid="scan-close">
                <X size={16} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {!scan.preview && !scan.proposed.length && (
                <>
                  <label
                    htmlFor="scan-file"
                    onDragOver={(e) => { e.preventDefault(); }}
                    onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) onScanFile(f); }}
                    className="block border border-dashed border-white/[0.12] hover:border-white/25 hover:bg-white/[0.02] transition-colors cursor-pointer p-10 text-center rounded-[2px]"
                    data-testid="scan-dropzone"
                  >
                    <Upload size={22} className="mx-auto text-neutral-400 mb-3" />
                    <div className="text-[0.9rem] font-medium mb-1">Drop your timetable here</div>
                    <div className="text-[0.75rem] text-neutral-500">or click to browse · PNG, JPG or WEBP · up to 8MB</div>
                  </label>
                  <input
                    id="scan-file"
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp"
                    className="hidden"
                    onChange={(e) => onScanFile(e.target.files?.[0])}
                    data-testid="scan-file-input"
                  />
                  <div className="text-[0.72rem] text-neutral-500 leading-relaxed">
                    Works best with clear photos or screenshots where day columns, class names and times are all legible. Lunch, break and free periods are automatically skipped.
                  </div>
                </>
              )}

              {scan.preview && !scan.proposed.length && (
                <div>
                  <div className="label-mono mb-2">/ preview</div>
                  <div className="border border-white/[0.06] p-2 rounded-[2px] bg-black/40">
                    <img src={scan.preview} alt="timetable preview" className="w-full max-h-[340px] object-contain rounded-[2px]" />
                  </div>
                  <div className="mt-2 text-[0.72rem] text-neutral-500 truncate">{scan.file?.name}</div>
                  <div className="mt-5 flex gap-2">
                    <button
                      onClick={() => setScan({ file: null, preview: null, busy: false, proposed: [], mode: scan.mode })}
                      className="btn-secondary flex-1"
                      disabled={scan.busy}
                      data-testid="scan-change"
                    >
                      Change file
                    </button>
                    <button
                      onClick={runScan}
                      className="btn-primary flex-1"
                      disabled={scan.busy}
                      data-testid="scan-run"
                    >
                      {scan.busy ? <><Loader2 size={13} className="animate-spin"/> Reading…</> : <><Wand2 size={13}/> Extract periods</>}
                    </button>
                  </div>
                  {scan.busy && (
                    <div className="mt-4 text-[0.72rem] text-neutral-500 font-mono text-center">
                      → sending to vision model · this may take 10-20s
                    </div>
                  )}
                </div>
              )}

              {scan.proposed.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="label-mono">/ detected</div>
                      <div className="font-display text-lg tightest font-semibold mt-1">
                        {scan.proposed.length} period{scan.proposed.length === 1 ? "" : "s"}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 p-1 border border-white/[0.08] rounded-[2px]">
                      <button
                        onClick={() => setScan((s) => ({ ...s, mode: "merge" }))}
                        className={`text-[0.72rem] px-2.5 py-1 rounded-[2px] transition-colors ${scan.mode === "merge" ? "bg-white text-black" : "text-neutral-400 hover:text-white"}`}
                        data-testid="scan-mode-merge"
                      >
                        Merge
                      </button>
                      <button
                        onClick={() => setScan((s) => ({ ...s, mode: "replace" }))}
                        className={`text-[0.72rem] px-2.5 py-1 rounded-[2px] transition-colors ${scan.mode === "replace" ? "bg-white text-black" : "text-neutral-400 hover:text-white"}`}
                        data-testid="scan-mode-replace"
                      >
                        Replace all
                      </button>
                    </div>
                  </div>

                  <div className="max-h-[42vh] overflow-y-auto pr-1 space-y-1">
                    {scan.proposed.map((p, i) => {
                      const c = subjectColor(p.subject);
                      return (
                        <div
                          key={i}
                          className="flex items-center gap-3 px-3 py-2.5 border border-white/[0.06] bg-white/[0.015] hover:border-white/15 transition-colors rounded-[2px]"
                          data-testid={`scan-slot-${i}`}
                        >
                          <div className="w-1 h-8 shrink-0 rounded-[1px]" style={{ background: c.border }} />
                          <div className="w-10 shrink-0 text-[0.72rem] font-mono text-neutral-400">{DAYS[p.day]}</div>
                          <div className="w-24 shrink-0 text-[0.72rem] font-mono tabular text-neutral-500">
                            {fmt(p.start_minute)}–{fmt(p.end_minute)}
                          </div>
                          <div className="flex-1 min-w-0 text-[0.86rem] truncate" style={{ color: c.text }}>{p.subject}</div>
                          <button
                            onClick={() => removeProposed(i)}
                            className="text-neutral-500 hover:text-rose-400 transition-colors shrink-0"
                            title="Remove"
                            data-testid={`scan-remove-${i}`}
                          >
                            <X size={13} />
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-3 text-[0.72rem] text-neutral-500 leading-relaxed">
                    {scan.mode === "replace"
                      ? "Replace all — your existing timetable will be wiped and rebuilt from this scan."
                      : "Merge — new periods are added on top of your existing timetable. Same-subject overlaps are merged; different-subject overlaps are skipped."}
                  </div>

                  <div className="mt-5 flex gap-2">
                    <button
                      onClick={() => setScan({ file: null, preview: null, busy: false, proposed: [], mode: scan.mode })}
                      className="btn-secondary flex-1"
                      disabled={scan.busy}
                      data-testid="scan-restart"
                    >
                      Start over
                    </button>
                    <button
                      onClick={applyScan}
                      className="btn-primary flex-1"
                      disabled={scan.busy || scan.proposed.length === 0}
                      data-testid="scan-apply"
                    >
                      {scan.busy ? <><Loader2 size={13} className="animate-spin"/> Applying…</> : <><Check size={13}/> {scan.mode === "replace" ? "Replace timetable" : `Add ${scan.proposed.length}`}</>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Timetable;
